import { api } from '../client';

export interface UserRole {
  id: string;
  userId: string;
  roleId: string;
  companyId?: string;
  role: {
    id: string;
    code: string;
    name: string;
  };
}

export interface User {
  id: string;
  email: string;
  fullName: string;
  companyId: string;
  company: {
    id: string;
    name: string;
    displayName: string;
    type?: string;
  };
  secondaryCompanyIds?: string[];
  department?: string;
  jobTitle?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'LOCKED' | 'PENDING_VERIFICATION';
  emailVerified: boolean;
  twoFactorEnabled?: boolean;
  lastLoginAt?: string;
  failedLoginAttempts?: number;
  lockedUntil?: string;
  userRoles: UserRole[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserDto {
  email: string;
  password: string;
  fullName: string;
  companyId: string;
  secondaryCompanyIds?: string[];
  department?: string;
  jobTitle?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'LOCKED' | 'PENDING_VERIFICATION';
  roleIds?: string[];
}

export interface QueryUserParams {
  search?: string;
  companyId?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'LOCKED' | 'PENDING_VERIFICATION';
  department?: string;
  roleId?: string;
  page?: number;
  limit?: number;
}

export const usersApi = {
  async getAll(params?: QueryUserParams) {
    const response = await api.get<{
      data: User[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/api/users', { params });
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get<User>(`/api/users/${id}`);
    return response.data;
  },

  async create(data: CreateUserDto) {
    const response = await api.post<User>('/api/users', data);
    return response.data;
  },

  async update(id: string, data: Partial<CreateUserDto>) {
    const response = await api.patch<User>(`/api/users/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await api.delete(`/api/users/${id}`);
    return response.data;
  },

  async assignRole(userId: string, roleId: string, companyId?: string) {
    const response = await api.post<User>(`/api/users/${userId}/roles`, {
      roleId,
      companyId,
    });
    return response.data;
  },

  async removeRole(userId: string, roleId: string) {
    const response = await api.delete<User>(`/api/users/${userId}/roles/${roleId}`);
    return response.data;
  },

  async unlockAccount(id: string) {
    const response = await api.post(`/api/users/${id}/unlock`);
    return response.data;
  },

  async changePassword(id: string, newPassword: string) {
    const response = await api.patch(`/api/users/${id}/password`, { newPassword });
    return response.data;
  },
};
